import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import PassoverMenuItem from './PassoverMenuItem';
import PassoverMenuDisclaimer from './PassoverMenuDisclaimer';
import MenuSkeleton from "./skeletons/MenuSkeleton";
import InputForm from './InputForm';
import ErrorMessageList from "./ErrorMessageList";
import messageMap from './i18n/messageMap.json';
import { menuAtom, totalPriceAtom } from './atoms';
import convertToDollar from "./utils/convertToDollar";


const PassoverMenu = () => {
    const [menu, setMenu] = useAtom(menuAtom);
    const [total] = useAtom(totalPriceAtom);
    const [menuError, setMenuError] = useState(undefined);
    const navigate = useNavigate();

    useEffect(() => {
        const getMenu = async () => {
            // don't refetch menu and overwrite user selections
            // if user is navigating back to menu
            if (menu?.length === 0) {
                try {
                    const resp = await fetch('/passover-menu/api/items');
                    if (!resp.ok) {
                        const responseText = await resp.text();
                        throw new Error(`HTTP error ${resp.status} - ${responseText}`);
                    } else {
                        const data = await resp.json();
                        setMenu(data.menu);
                    }
                } catch (error) {
                    console.error("Error fetching menu items ", error);
                    setMenuError(true);
                }
            }
        }

        getMenu();
    }, []);

    const updateQuantity = useCallback((options, itemId) => {
        setMenu((prevMenu) => {
            return prevMenu.map((menuItem) => {
                if (menuItem.id === itemId) {
                    const newQuantity = options.add ? menuItem.quantity + 1 : menuItem.quantity - 1;
                    return {
                        ...menuItem, 
                        quantity: newQuantity,
                        cost: newQuantity * menuItem.price,
                    }
                } else {
                    return menuItem
                }
            })
        })
    }, []);

    // Pass to InputForm component
    // If server-side validation is good,
    // take user to confirmation page
    const onSuccessCallback = useCallback(() => {
        navigate('/confirmation');
    }, [navigate]);

    const requiredFormFields = useMemo(() => ({ name: true, phoneNumber: true, emailAddress: true }), [])

    let display = undefined;
    if (menuError) {
        display = (
            <ErrorMessageList className="menu-load-error-msgs" messages={[{
                msg: messageMap.loadingMenuFailure
            }]}/>
        )
    } else if (menu.length > 0) {
        display = (
            <ul>
                {menu.map((item) => {
                    return <PassoverMenuItem key={item.id}
                                            id={item.id}
                                            name={item.name}
                                            price={item.price}
                                            unit={item.unit}
                                            quantity={item.quantity}
                                            updateQuantity={updateQuantity}
                                            />}
                )}
            </ul>
        )
    } else {
        display = (
            <MenuSkeleton />
        );
    }

    return (
        <div id="passover-menu-page">
            <PassoverMenuDisclaimer hideSederNotice={true} />
            {display}
            {
                (!menuError && menu.length > 0) &&
                (
                    <Fragment>
                        <h4 className="passover-menu-total">Total: {convertToDollar(total)}</h4>
                        <div className="passover-menu-customer-form">
                            <h4 className="form-title">Please enter your contact information below:</h4>
                            <InputForm
                                onSuccessCallback={onSuccessCallback}
                                requiredFields={requiredFormFields}
                                validate={false}
                                />
                        </div>
                    </Fragment>
                )
            }
        </div>
    )
}

export default PassoverMenu;
